import { useState, useEffect } from "react";
import { User, ShoppingBag, Heart, Shield } from "lucide-react";
import {
  Popover,
  PopoverContent,
} from "@/components/ui/popover";

interface TourStep {
  icon: typeof User;
  title: string;
  description: string;
}

const userTourSteps: TourStep[] = [
  {
    icon: User,
    title: "פרטים אישיים",
    description: "כאן תוכל לערוך את הפרטים האישיים שלך, כולל שם, טלפון וכתובת למשלוח.",
  },
  {
    icon: ShoppingBag,
    title: "ההזמנות שלי",
    description: "צפה בהיסטוריית ההזמנות שלך ועקוב אחר המשלוחים בקלות.",
  },
  {
    icon: Heart,
    title: "המועדפים שלי",
    description: "כל המוצרים שסימנת כמועדפים נמצאים כאן. תוכל להוסיף אותם לסל בקלות.",
  },
];

const adminTourSteps: TourStep[] = [
  ...userTourSteps,
  {
    icon: Shield,
    title: "ניהול הודעות",
    description: "כמנהל, תוכל לצפות ולנהל את כל ההודעות שנשלחו דרך טופס יצירת הקשר.",
  },
];

interface ProfileTourProps {
  isAdmin: boolean;
  activeTab: string;
  onComplete: () => void;
}

export const ProfileTour = ({ isAdmin, activeTab, onComplete }: ProfileTourProps) => {
  const [currentTip, setCurrentTip] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  
  const tourSteps = isAdmin ? adminTourSteps : userTourSteps;

  useEffect(() => {
    // Find the index based on active tab
    const tabIndex = ["details", "orders", "favorites", "admin"].indexOf(activeTab);
    if (tabIndex >= 0 && tabIndex < tourSteps.length) {
      setCurrentTip(tabIndex);
    }
  }, [activeTab, tourSteps.length]);

  useEffect(() => {
    // Update anchor element when currentTip changes
    const getCurrentAnchorElement = () => {
      const tabs = ["details", "orders", "favorites"];
      if (isAdmin) tabs.push("admin");
      return tabs[currentTip];
    };
    
    const element = document.getElementById(`tour-anchor-${getCurrentAnchorElement()}`);
    setAnchorElement(element);
  }, [currentTip, isAdmin]);

  const handleNavigation = () => {
    if (currentTip === tourSteps.length - 1) {
      setIsOpen(false);
      onComplete();
    } else {
      setCurrentTip(currentTip + 1);
    }
  };

  if (!isOpen || !anchorElement) {
    return null;
  }

  // Get position of anchor element
  const rect = anchorElement.getBoundingClientRect();

  return (
    <div
      style={{
        position: "fixed",
        top: rect.top + rect.height / 2,
        left: rect.left - 320,
        zIndex: 100,
      }}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverContent
          className="max-w-[280px] py-3 shadow-lg"
          side="left"
          showArrow={true}
          align="center"
          sideOffset={20}
        >
          <div className="space-y-3" dir="rtl">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                {(() => {
                  const IconComponent = tourSteps[currentTip].icon;
                  return <IconComponent className="h-5 w-5 text-primary" />;
                })()}
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-[13px] font-medium">{tourSteps[currentTip].title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {tourSteps[currentTip].description}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {currentTip + 1}/{tourSteps.length}
              </span>
              <button
                className="text-xs font-medium text-primary hover:underline"
                onClick={handleNavigation}
              >
                {currentTip === tourSteps.length - 1 ? "סיימתי" : "הבא"}
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
